import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, FileCheck, DollarSign, Users, ArrowRight } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Link } from "react-router-dom";
import i18n from "@/i18n";

const formatCurrency = (value) => {
  const locale = i18n.language === 'en' ? 'en-US' : 'es-CO';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    currencyDisplay: 'narrowSymbol'
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const locale = i18n.language === 'en' ? 'en-US' : 'es-CO';
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
};

export default function Dashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    facturas_mes: 0,
    documentos_mes: 0,
    total_facturado: 0,
    proveedores_registrados: 0
  });
  const [chartData, setChartData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, chartRes, activityRes] = await Promise.all([
          axios.get(`${API}/stats/dashboard`),
          axios.get(`${API}/stats/chart-data`),
          axios.get(`${API}/stats/recent-activity`)
        ]);
        setStats(statsRes.data);
        setChartData(chartRes.data);
        setRecentActivity(activityRes.data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const kpiCards = [
    {
      title: t('dashboard.invoicesThisMonth'),
      value: stats.facturas_mes,
      icon: FileText,
      color: "bg-blue-100 text-blue-600"
    },
    {
      title: t('dashboard.supportDocsThisMonth'),
      value: stats.documentos_mes,
      icon: FileCheck,
      color: "bg-amber-100 text-amber-600"
    },
    {
      title: t('dashboard.totalBilled'),
      value: formatCurrency(stats.total_facturado),
      icon: DollarSign,
      color: "bg-green-100 text-green-600"
    },
    {
      title: t('dashboard.registeredSuppliers'),
      value: stats.proveedores_registrados,
      icon: Users,
      color: "bg-purple-100 text-purple-600"
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard" className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-['Barlow']">{t('dashboard.title')}</h1>
        <p className="text-slate-500 mt-1">{t('dashboard.subtitle')}</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiCards.map((kpi, index) => {
          const Icon = kpi.icon;
          return (
            <Card key={index} data-testid={`kpi-card-${index}`} className="border-slate-200 hover:shadow-md transition-shadow duration-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">{kpi.title}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-2 font-['Barlow']">{kpi.value}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${kpi.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Line Chart - Facturas */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900 font-['Barlow']">
              {t('dashboard.electronicInvoices')}
            </CardTitle>
            <p className="text-sm text-slate-500">{t('dashboard.last6Months')}</p>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="mes" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '13px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="facturas" 
                    stroke="#0f2a4a" 
                    strokeWidth={2}
                    dot={{ fill: '#0f2a4a', strokeWidth: 2 }}
                    name={t('dashboard.invoices')}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Bar Chart - Documentos Soporte */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900 font-['Barlow']">
              {t('dashboard.supportDocuments')}
            </CardTitle>
            <p className="text-sm text-slate-500">{t('dashboard.last6Months')}</p>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="mes" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '13px'
                    }}
                  />
                  <Bar 
                    dataKey="documentos" 
                    fill="#f59e0b" 
                    radius={[4, 4, 0, 0]}
                    name={t('dashboard.documents')}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold text-slate-900 font-['Barlow']">
              {t('dashboard.recentActivity')}
            </CardTitle>
            <p className="text-sm text-slate-500">{t('dashboard.latestInvoicesAndDocs')}</p>
          </div>
          <Link 
            to="/facturas" 
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            {t('dashboard.viewAll')} <ArrowRight className="w-4 h-4" />
          </Link>
        </CardHeader>
        <CardContent>
          {recentActivity.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>{t('dashboard.noRecentActivity')}</p>
            </div>
          ) : (
            <div className="space-y-1">
              {recentActivity.map((item, index) => (
                <div 
                  key={index}
                  data-testid={`activity-item-${index}`}
                  className="flex items-center gap-4 py-3 px-2 rounded-lg hover:bg-slate-50 transition-colors duration-150"
                >
                  <div className={`p-2.5 rounded-lg ${
                    item.tipo === 'factura' 
                      ? 'bg-blue-100 text-blue-600' 
                      : 'bg-amber-100 text-amber-600'
                  }`}>
                    {item.tipo === 'factura' ? (
                      <FileText className="w-4 h-4" />
                    ) : (
                      <FileCheck className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {item.numero} - {item.nombre}
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatDate(item.fecha)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-slate-900">
                      {formatCurrency(item.valor)}
                    </p>
                    <span className={`inline-block text-xs px-2 py-0.5 rounded ${
                      item.tipo === 'factura' 
                        ? 'bg-blue-50 text-blue-600' 
                        : 'bg-amber-50 text-amber-600'
                    }`}>
                      {item.tipo === 'factura' ? t('dashboard.invoice') : t('dashboard.supportDoc')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
